import React from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Breadcrumbs,
  Link,
} from '@mui/material';
import StoreIcon from '@mui/icons-material/Store';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { useStore } from '../../hooks/useStores';
import { useProductsByLocation } from '../../hooks/useProducts';
import { getImageUrl } from '../../types/product';
import { useCartStore } from '../../stores/cartStore';

export const StoreDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: store, isLoading: storeLoading, error: storeError } = useStore(id!);
  const { data: products, isLoading: productsLoading, error: productsError } = useProductsByLocation(id!);
  const addItem = useCartStore((state) => state.addItem);

  const handleAddToCart = (product: NonNullable<typeof products>[0]) => {
    addItem({
      productId: product.id,
      name: product.name,
      price: typeof product.price === 'string' ? parseFloat(product.price) : product.price,
      quantity: 1,
      locationId: product.locationId,
      locationName: store?.name || 'Unknown Store',
      image: product.images?.[0]?.thumbnail,
      organizationId: product.organizationId,
    });
  };

  if (storeLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (storeError || !store) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">Store not found or failed to load.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/" underline="hover" color="inherit">
          Home
        </Link>
        <Link component={RouterLink} to="/stores" underline="hover" color="inherit">
          Stores
        </Link>
        <Typography color="text.primary">{store.name}</Typography>
      </Breadcrumbs>

      {/* Store Header */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={3}>
            <Box
              sx={{
                width: '100%',
                height: 200,
                bgcolor: 'primary.light',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <StoreIcon sx={{ fontSize: 100, color: 'white' }} />
            </Box>
          </Grid>
          <Grid item xs={12} md={9}>
            <Typography variant="h3" gutterBottom fontWeight="bold">
              {store.name}
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Chip label={store.type} color="primary" sx={{ mr: 1 }} />
              {store.isActive && <Chip label="Open" color="success" />}
            </Box>
            <Typography variant="body1" paragraph>
              {store.description || 'No description available'}
            </Typography>

            <Grid container spacing={2}>
              {store.address && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Address:</strong> {store.address}
                  </Typography>
                </Grid>
              )}
              {store.city && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Location:</strong> {store.city}, {store.wilaya || store.country}
                  </Typography>
                </Grid>
              )}
              {store.phone && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Phone:</strong> {store.phone}
                  </Typography>
                </Grid>
              )}
              {store.email && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Email:</strong> {store.email}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Products Section */}
      <Box>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          <LocalOfferIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Products from this store
        </Typography>

        {productsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {productsError && (
          <Alert severity="error" sx={{ mb: 4 }}>
            Failed to load products. Please try again later.
          </Alert>
        )}

        {!productsLoading && products?.length === 0 && (
          <Alert severity="info">
            This store doesn't have any products listed yet.
          </Alert>
        )}

        <Grid container spacing={3} sx={{ mt: 2 }}>
          {products?.map((product) => (
            <Grid item xs={12} sm={6} md={4} key={product.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)' },
                }}
              >
                <Box
                  component={RouterLink}
                  to={`/products/${product.id}`}
                  sx={{
                    height: 200,
                    bgcolor: 'grey.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  {product.images && product.images.length > 0 ? (
                    <Box
                      component="img"
                      src={getImageUrl(product.images[0].thumbnail)}
                      alt={product.name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        p: 2,
                        transition: 'transform 0.3s ease',
                        '&:hover': { transform: 'scale(1.05)' },
                      }}
                    />
                  ) : (
                    <Inventory2OutlinedIcon sx={{ fontSize: 60, color: 'grey.400' }} />
                  )}
                </Box>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" gutterBottom noWrap>
                    {product.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {product.description || 'No description available'}
                  </Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {typeof product.price === 'string' ? parseFloat(product.price).toFixed(2) : (product.price?.toFixed(2) || '0.00')} DZD
                  </Typography>
                  {product.category && (
                    <Chip label={product.category} size="small" sx={{ mt: 1 }} />
                  )}
                </CardContent>
                <Box sx={{ p: 2, pt: 0 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AddOutlinedIcon />}
                    onClick={() => handleAddToCart(product)}
                  >
                    Add to Cart
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};
